import {
    FileText,
    Car,
    DoorOpen,
    ShoppingBag,
    Package,
    AlertTriangle,
    UserPlus,
    UserMinus,
    ArrowLeftRight,
    Laptop,
    Mail,
    Bell,
    Receipt,
    MessageCircle,
    Ticket,
    Zap,
    BookOpen,
    KanbanSquare,
    Clock,
    MessageSquare,
    CheckCircle,
    Coffee,
    HeartPulse,
    ClipboardCheck,
    PlayCircle
} from 'lucide-react';
import axiosInstance from './axiosConfig';

/**
 * Mapa de referencia_tipo → ruta en la app.
 */
const ROUTE_MAP = {
    mis_tareas: '/proyectos/mis-tareas',
    publicacion: '/inicio',
    alerta_sismo: '/inicio',
    jornada_certificados: '/inicio',
    reporte_calidad: '/inicio',
    solicitud_taxi: '/solicitud-taxi',
    reserva_sala: '/reserva-salas',
    candidato: '/vacantes',
    empleado: '/empleados',
    evento: '/eventos',
    venta_especial: '/productos-sura',
    pedido_venta: '/productos-sura',
    producto_venta: '/productos-sura',
    encuesta_producto: '/encuestas-producto',
    asset: '/equipos',
    correo: '/ajustes',
    viatico: '/solicitud-viaticos?tab=aprobar',
    calendario_cierre: '/calendario-cierre',
    ticket: '/soporte-ti',
    taller_curso: '/talleres-cursos',
    requisicion: '/requisicion-compras',
    papeleria: '/solicitud-papeleria',
    tarea: '/proyectos',
    proyecto: '/proyectos',
    comida: '/solicitud-comida',
    calidad_diagnostico: '/sura-reporta',
    puerta: '/puertas',
};

/**
 * Configuración visual por `tipo` de notificación.
 * - icon: componente lucide-react
 * - color: clase tailwind para el icono (en lista)
 * - accent: color hex para el acento del toast (borde, progress bar, glow)
 * - bg: clase tailwind para fondo del icono en lista
 */
const TYPE_CONFIG = {
    // Publicaciones
    nueva_publicacion: { icon: FileText, color: 'text-blue-500', accent: '#3b82f6', bg: 'bg-blue-50' },
    // Alerta de seguridad (sismo)
    alerta_sismo: { icon: AlertTriangle, color: 'text-red-600', accent: '#C0392B', bg: 'bg-red-50' },
    // Jornada de certificados de salud
    jornada_certificados: { icon: HeartPulse, color: 'text-rose-500', accent: '#f43f5e', bg: 'bg-rose-50' },
    // Guía de reporte de calidad
    reporte_calidad: { icon: ClipboardCheck, color: 'text-emerald-500', accent: '#10B981', bg: 'bg-emerald-50' },
    // Taxi
    taxi_solicitado: { icon: Car, color: 'text-yellow-600', accent: '#d97706', bg: 'bg-yellow-50' },
    taxi_aprobacion_pendiente: { icon: Car, color: 'text-amber-500', accent: '#f59e0b', bg: 'bg-amber-50' },
    taxi_nueva_solicitud: { icon: Car, color: 'text-yellow-600', accent: '#d97706', bg: 'bg-yellow-50' },
    taxi_aprobado: { icon: Car, color: 'text-green-500', accent: '#22c55e', bg: 'bg-green-50' },
    taxi_confirmado: { icon: Car, color: 'text-green-500', accent: '#22c55e', bg: 'bg-green-50' },
    taxi_rechazado: { icon: Car, color: 'text-red-500', accent: '#ef4444', bg: 'bg-red-50' },
    taxi_cancelado: { icon: Car, color: 'text-red-500', accent: '#ef4444', bg: 'bg-red-50' },
    // Salas
    sala_reservada: { icon: DoorOpen, color: 'text-teal-500', accent: '#14b8a6', bg: 'bg-teal-50' },
    sala_cancelada: { icon: DoorOpen, color: 'text-red-500', accent: '#ef4444', bg: 'bg-red-50' },
    sala_aprobada: { icon: DoorOpen, color: 'text-green-500', accent: '#22c55e', bg: 'bg-green-50' },
    // Puertas: avisos de lo que pasa en una puerta (reglas en /puertas)
    puerta_aviso: { icon: DoorOpen, color: 'text-cyan-600', accent: '#06b6d4', bg: 'bg-cyan-50' },
    // Ventas / Pedidos
    venta_nueva: { icon: ShoppingBag, color: 'text-pink-500', accent: '#ec4899', bg: 'bg-pink-50' },
    pedido_nuevo: { icon: Package, color: 'text-indigo-500', accent: '#6366f1', bg: 'bg-indigo-50' },
    pedido_actualizado: { icon: Package, color: 'text-blue-500', accent: '#3b82f6', bg: 'bg-blue-50' },
    pedido_aprobado: { icon: Package, color: 'text-green-500', accent: '#22c55e', bg: 'bg-green-50' },
    pedido_rechazado: { icon: Package, color: 'text-red-500', accent: '#ef4444', bg: 'bg-red-50' },
    pedido_entregado: { icon: Package, color: 'text-indigo-500', accent: '#6366f1', bg: 'bg-indigo-50' },
    pago_reportado: { icon: Receipt, color: 'text-emerald-500', accent: '#10b981', bg: 'bg-emerald-50' },
    stock_bajo: { icon: AlertTriangle, color: 'text-orange-500', accent: '#f97316', bg: 'bg-orange-50' },
    liquidacion_pendiente: { icon: Receipt, color: 'text-amber-500', accent: '#f59e0b', bg: 'bg-amber-50' },
    liquidacion_resuelta: { icon: Receipt, color: 'text-emerald-500', accent: '#10b981', bg: 'bg-emerald-50' },
    // Eventos
    evento_critico: { icon: AlertTriangle, color: 'text-red-500', accent: '#ef4444', bg: 'bg-red-50' },
    evento_resuelto: { icon: AlertTriangle, color: 'text-green-500', accent: '#22c55e', bg: 'bg-green-50' },
    // Candidatos
    candidato_nuevo: { icon: UserPlus, color: 'text-cyan-500', accent: '#06b6d4', bg: 'bg-cyan-50' },
    candidato_actualizado: { icon: UserPlus, color: 'text-cyan-500', accent: '#06b6d4', bg: 'bg-cyan-50' },
    encuesta_invitacion: { icon: FileText, color: 'text-sky-500', accent: '#0ea5e9', bg: 'bg-sky-50' },
    encuesta_respuesta: { icon: FileText, color: 'text-emerald-500', accent: '#10b981', bg: 'bg-emerald-50' },
    // Empleados
    empleado_egreso: { icon: UserMinus, color: 'text-red-400', accent: '#f87171', bg: 'bg-red-50' },
    empleado_movimiento: { icon: ArrowLeftRight, color: 'text-emerald-500', accent: '#10b981', bg: 'bg-emerald-50' },
    // IT
    ficha_its: { icon: FileText, color: 'text-orange-500', accent: '#f97316', bg: 'bg-orange-50' },
    equipo_asignado: { icon: Laptop, color: 'text-purple-500', accent: '#a855f7', bg: 'bg-purple-50' },
    correo_creado: { icon: Mail, color: 'text-green-500', accent: '#22c55e', bg: 'bg-green-50' },
    // Viáticos
    viatico_pendiente: { icon: Receipt, color: 'text-amber-500', accent: '#f59e0b', bg: 'bg-amber-50' },
    viatico_aprobado: { icon: Receipt, color: 'text-green-500', accent: '#22c55e', bg: 'bg-green-50' },
    viatico_rechazado: { icon: Receipt, color: 'text-red-500', accent: '#ef4444', bg: 'bg-red-50' },
    viatico_reenviado: { icon: Receipt, color: 'text-amber-500', accent: '#f59e0b', bg: 'bg-amber-50' },
    viatico_por_pagar: { icon: Receipt, color: 'text-indigo-500', accent: '#6366f1', bg: 'bg-indigo-50' },
    viatico_pagado: { icon: Receipt, color: 'text-indigo-500', accent: '#6366f1', bg: 'bg-indigo-50' },
    viatico_legalizado: { icon: Receipt, color: 'text-emerald-500', accent: '#10b981', bg: 'bg-emerald-50' },
    // Requisiciones de compra
    requisicion_pendiente: { icon: ShoppingBag, color: 'text-amber-500', accent: '#f59e0b', bg: 'bg-amber-50' },
    requisicion_aprobada: { icon: ShoppingBag, color: 'text-blue-500', accent: '#3b82f6', bg: 'bg-blue-50' },
    requisicion_rechazada: { icon: ShoppingBag, color: 'text-red-500', accent: '#ef4444', bg: 'bg-red-50' },
    requisicion_en_gestion: { icon: ShoppingBag, color: 'text-purple-500', accent: '#a855f7', bg: 'bg-purple-50' },
    requisicion_completada: { icon: ShoppingBag, color: 'text-green-500', accent: '#22c55e', bg: 'bg-green-50' },
    // Tickets de soporte
    ticket_nuevo: { icon: Ticket, color: 'text-blue-500', accent: '#3b82f6', bg: 'bg-blue-50' },
    ticket_comentario: { icon: Ticket, color: 'text-violet-500', accent: '#8b5cf6', bg: 'bg-violet-50' },
    ticket_estado: { icon: Ticket, color: 'text-amber-500', accent: '#f59e0b', bg: 'bg-amber-50' },
    ticket_asignado: { icon: Ticket, color: 'text-teal-500', accent: '#14b8a6', bg: 'bg-teal-50' },
    // Comida
    comida_pendiente: { icon: Coffee, color: 'text-amber-500', accent: '#f59e0b', bg: 'bg-amber-50' },
    comida_aprobada: { icon: Coffee, color: 'text-blue-500', accent: '#3b82f6', bg: 'bg-blue-50' },
    comida_rechazada: { icon: Coffee, color: 'text-red-500', accent: '#ef4444', bg: 'bg-red-50' },
    comida_completada: { icon: Coffee, color: 'text-green-500', accent: '#22c55e', bg: 'bg-green-50' },
    comida_cancelada: { icon: Coffee, color: 'text-gray-500', accent: '#6b7280', bg: 'bg-gray-50' },
    // Talleres y cursos
    taller_asignado: { icon: BookOpen, color: 'text-blue-500', accent: '#3b82f6', bg: 'bg-blue-50' },
    // Proyectos / tareas
    task_assigned:       { icon: KanbanSquare, color: 'text-cyan-600',  accent: '#06b6d4', bg: 'bg-cyan-50' },
    task_status_changed: { icon: CheckCircle,  color: 'text-cyan-600',  accent: '#06b6d4', bg: 'bg-cyan-50' },
    task_commented:      { icon: MessageSquare, color: 'text-violet-500', accent: '#8b5cf6', bg: 'bg-violet-50' },
    task_due_soon:       { icon: Clock,        color: 'text-amber-500', accent: '#f59e0b', bg: 'bg-amber-50' },
    task_overdue:        { icon: AlertTriangle, color: 'text-rose-500',  accent: '#ef4444', bg: 'bg-rose-50' },
    task_mentioned:      { icon: MessageSquare, color: 'text-violet-500', accent: '#8b5cf6', bg: 'bg-violet-50' },
    task_digest:         { icon: AlertTriangle, color: 'text-rose-500',  accent: '#ef4444', bg: 'bg-rose-50' },
    task_unblocked:      { icon: PlayCircle,   color: 'text-indigo-500', accent: '#6366f1', bg: 'bg-indigo-50' },
};

const DEFAULT_CONFIG = { icon: Bell, color: 'text-gray-500', accent: '#64748b', bg: 'bg-gray-50' };

export const CHAT_CONFIG = { icon: MessageCircle, color: 'text-violet-500', accent: '#8b5cf6', bg: 'bg-violet-50' };
export const BUZZ_CONFIG = { icon: Zap, color: 'text-amber-500', accent: '#f59e0b', bg: 'bg-amber-50' };

/**
 * Obtiene la ruta de navegación para una notificación.
 * Rutas inteligentes: según el tipo de notificación, lleva al lugar correcto.
 */
export function getNotificationRoute(notification) {
    const refTipo = notification.referencia_tipo;
    const tipo = notification.tipo;

    // Liquidación de Productos SURA: GH responde en un modal, sin entrar al módulo
    if (refTipo === 'pedido_liquidacion' && notification.referencia_id) {
        return `/notificaciones?liquidacion=${notification.referencia_id}`;
    }

    // Taxi: rutas inteligentes según contexto
    if (refTipo === 'solicitud_taxi') {
        if (tipo === 'taxi_aprobacion_pendiente') return '/solicitud-taxi?tab=aprobar';
        if (tipo === 'taxi_nueva_solicitud') return '/solicitud-taxi?tab=manage';
        // Confirmado, aprobado, rechazado, cancelado → historial del solicitante
        return '/solicitud-taxi?tab=history';
    }

    // Viáticos: rutas según contexto
    if (refTipo === 'viatico') {
        if (tipo === 'viatico_pendiente' || tipo === 'viatico_reenviado') return '/solicitud-viaticos?tab=aprobar';
        if (tipo === 'viatico_por_pagar' || tipo === 'viatico_legalizado') return '/solicitud-viaticos?tab=gestion';
        return '/solicitud-viaticos';
    }

    // Requisición: rutas según contexto
    if (refTipo === 'requisicion') {
        if (tipo === 'requisicion_pendiente') return '/requisicion-compras?tab=aprobar';
        if (tipo === 'requisicion_aprobada' && notification.mensaje?.includes('lista para gestión')) return '/requisicion-compras?tab=procura';
        return '/requisicion-compras';
    }

    // Sura Reporta (Calidad): abre el reporte con su seguimiento y conversación
    if (refTipo === 'calidad_diagnostico') {
        return notification.referencia_id ? `/sura-reporta?reporte=${notification.referencia_id}` : '/sura-reporta';
    }

    // Papelería: rutas según contexto
    if (refTipo === 'papeleria') {
        if (tipo === 'papeleria_nueva') return '/solicitud-papeleria?tab=bandeja';
        return '/solicitud-papeleria?tab=list';
    }

    // Comida: rutas según contexto
    if (refTipo === 'comida') {
        if (tipo === 'comida_pendiente') return '/solicitud-comida?tab=aprobaciones';
        if (tipo === 'comida_aprobada') return '/solicitud-comida?tab=bandeja';
        return '/solicitud-comida?tab=list';
    }

    // Egreso y movimiento interno: abren el cierre de sus cuentas (TI)
    if (tipo === 'empleado_egreso' && notification.referencia_id) {
        return `/empleados?cierre=${notification.referencia_id}`;
    }
    if (tipo === 'empleado_movimiento' && notification.referencia_id) {
        return `/empleados?cierre=${notification.referencia_id}&tipo=movimiento`;
    }

    // Ficha TI: abre el wizard de Onboarding TI de ese empleado
    if (tipo === 'ficha_its' && notification.referencia_id) {
        return `/empleados?onboarding=${notification.referencia_id}`;
    }

    // Ticket: abrir directamente el detalle del ticket referenciado
    if (refTipo === 'ticket' && notification.referencia_id) {
        return `/soporte-ti?ticket=${notification.referencia_id}`;
    }

    // Tarea: necesita resolverse async (slug + numero). Fallback al modulo.
    if (refTipo === 'tarea') {
        return '/proyectos/mis-tareas';
    }

    return ROUTE_MAP[refTipo] || '/notificaciones';
}

/**
 * Resuelve la URL real para una notificación de tarea, haciendo fetch al backend
 * para obtener slug del proyecto y número de tarea. Si falla, devuelve fallback.
 */
export async function resolveNotificationNavigation(notification) {
    if (notification.referencia_tipo === 'tarea' && notification.referencia_id) {
        try {
            const { data } = await axiosInstance.get(`/api/tareas/${notification.referencia_id}`);
            if (data?.proyecto_slug && data?.numero) {
                return `/proyectos/${data.proyecto_slug}/tareas/${data.numero}`;
            }
        } catch (err) {
            // Si la tarea no existe o no tengo acceso, caigo a "Mis tareas"
        }
        return '/proyectos/mis-tareas';
    }
    if (notification.referencia_tipo === 'proyecto' && notification.referencia_id) {
        try {
            const { data } = await axiosInstance.get(`/api/proyectos/${notification.referencia_id}`);
            if (data?.slug) return `/proyectos/${data.slug}`;
        } catch (err) {
            // Sin acceso o proyecto borrado: al listado
        }
        return '/proyectos';
    }
    return getNotificationRoute(notification);
}

/**
 * Obtiene la configuración visual para un tipo de notificación.
 */
export function getNotificationConfig(tipo) {
    if (!tipo) return DEFAULT_CONFIG;

    if (TYPE_CONFIG[tipo]) return TYPE_CONFIG[tipo];

    const prefixes = ['taxi_', 'sala_', 'pedido_', 'candidato_', 'viatico_', 'ticket_', 'requisicion_'];
    for (const prefix of prefixes) {
        if (tipo.startsWith(prefix)) {
            const firstMatch = Object.keys(TYPE_CONFIG).find(k => k.startsWith(prefix));
            if (firstMatch) return TYPE_CONFIG[firstMatch];
        }
    }

    return DEFAULT_CONFIG;
}

/**
 * Reproduce un sonido corto de notificación usando Web Audio API.
 * Genera un tono amigable tipo "ding" sin necesidad de archivos de audio.
 */
let _audioCtx = null;

export function playNotificationSound() {
    try {
        if (!_audioCtx) {
            _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        }
        const ctx = _audioCtx;

        // Primer tono (nota alta)
        const osc1 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(880, ctx.currentTime);       // A5
        osc1.frequency.setValueAtTime(1108.73, ctx.currentTime + 0.08); // C#6
        gain1.gain.setValueAtTime(0.15, ctx.currentTime);
        gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
        osc1.connect(gain1);
        gain1.connect(ctx.destination);
        osc1.start(ctx.currentTime);
        osc1.stop(ctx.currentTime + 0.35);

        // Segundo tono (armonía sutil)
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(1318.51, ctx.currentTime + 0.06); // E6
        gain2.gain.setValueAtTime(0, ctx.currentTime);
        gain2.gain.setValueAtTime(0.08, ctx.currentTime + 0.06);
        gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.start(ctx.currentTime + 0.06);
        osc2.stop(ctx.currentTime + 0.4);
    } catch (e) {
        // Silently fail — audio not critical
    }
}
